/**
 * 撤销/重做命令栈（架构书 §10.3）。纯逻辑、不依赖 React，方便直接单元测试。
 *
 * **"已保存后再撤销 = 发送一条新的补偿命令，不是回滚"**：`undo()`/`redo()` 都是
 * 重新调用一次真实的写路径（`PATCH /layouts/geometry` 或 `POST /locations`），
 * 服务端历史因此保持线性、每一步都可审计——不存在"客户端悄悄把数据库改回去"
 * 这种操作。这也是为什么创建位置的撤销是"新建一条归档命令"而不是"假装这条
 * 创建从未发生过"：redo 再次调用 `do()` 会创建一条**新的** location 记录
 * （新 id），而不是把刚归档的那条复活——V1 没有 unarchive 端点（见
 * `04-open-decisions.md` D-21），这是有意的行为，不是 bug。
 */
export interface Command {
  readonly label: string;
  /** 结构变更（创建/归档），供 UI 提示"这一步涉及位置结构，不只是几何" */
  readonly structural?: boolean;
  do(): void | Promise<void>;
  undo(): void | Promise<void>;
}

/** 架构书 §10.3："本地栈 100 步"。 */
export const HISTORY_LIMIT = 100;

export class CommandStack {
  private past: Command[] = [];
  private future: Command[] = [];
  private readonly limit: number;
  private readonly onChange: () => void;

  constructor(opts: { readonly limit?: number; readonly onChange?: () => void } = {}) {
    this.limit = opts.limit ?? HISTORY_LIMIT;
    this.onChange = opts.onChange ?? (() => undefined);
  }

  get canUndo(): boolean {
    return this.past.length > 0;
  }

  get canRedo(): boolean {
    return this.future.length > 0;
  }

  get undoLabel(): string | null {
    return this.past.at(-1)?.label ?? null;
  }

  get redoLabel(): string | null {
    return this.future.at(-1)?.label ?? null;
  }

  get depth(): number {
    return this.past.length;
  }

  /** 执行一条新命令：清空 redo 栈（分支历史不保留，与大多数编辑器一致）。 */
  async execute(cmd: Command): Promise<void> {
    await cmd.do();
    this.past.push(cmd);
    if (this.past.length > this.limit) this.past.shift();
    this.future = [];
    this.onChange();
  }

  /**
   * M3B 独立复审第二轮 NEW-3：先 `pop()` 再 `await cmd.undo()` 的话，一旦
   * `undo()` 中途失败（比如 `createBatchCommand` 补偿归档时网络断了，
   * 见该文件的 `PartialBatchCreateCompensationFailedError`），命令已经从
   * `past` 里被摘掉，但因为异常提前退出、还没跑到 `this.future.push(cmd)`，
   * 它也进不了 `future`——这条命令从两个栈里都消失了，用户既撤不掉也重做
   * 不了。改成只 `peek`，只有 `cmd.undo()` 真正成功才提交栈的变化；失败时
   * `past` 原样保留这条命令，可以再点一次撤销重试。
   */
  async undo(): Promise<void> {
    const cmd = this.past.at(-1);
    if (!cmd) return;
    await cmd.undo();
    this.past.pop();
    this.future.push(cmd);
    this.onChange();
  }

  async redo(): Promise<void> {
    const cmd = this.future.at(-1);
    if (!cmd) return;
    await cmd.do();
    this.future.pop();
    this.past.push(cmd);
    this.onChange();
  }

  clear(): void {
    this.past = [];
    this.future = [];
    this.onChange();
  }
}

/**
 * `do()` 部分失败（比如批量粘贴 3 个对象，第 2 个校验不过）之后，已经成功
 * 创建的那些如果连补偿归档都失败了——不能装作什么都没发生。调用方需要用
 * 专门的文案提示用户去手动核实/清理，而不是把它当成一次普通的创建失败。
 */
export class PartialBatchCreateCompensationFailedError extends Error {
  constructor(
    readonly attemptedCount: number,
    readonly sourceError: unknown,
  ) {
    super('PARTIAL_BATCH_CREATE_COMPENSATION_FAILED');
    this.name = 'PartialBatchCreateCompensationFailedError';
  }
}

/**
 * 批量创建命令（duplicate / 粘贴 / 画布新建）：`do()` 依次调用 `create`。
 *
 * **M3B 独立复审 M-3**：旧实现里，如果批量创建中途某一项失败，前面已经成功
 * 创建的那些位置就成了孤儿——`do()` 的 promise 直接 reject，`CommandStack.
 * execute()` 因此从不会把这条命令压入历史（见 `CommandStack.execute` 的
 * `await cmd.do(); this.past.push(cmd);` 顺序），已创建的对象既不在撤销栈里
 * 也没有触发场景刷新，用户看不见也撤不掉。
 *
 * 修复：`do()` 中途失败时，立即把本次已经成功创建的那些补偿归档掉，再把
 * 原始错误重新抛出——这条命令要么整体发生要么整体没发生，不存在"看不见也
 * 撤销不了"的中间态。如果连补偿归档本身都失败（比如网络在那一刻整个断了），
 * 才会抛出 `PartialBatchCreateCompensationFailedError`，明确告诉用户去手动
 * 核实，而不是静默吞掉。
 *
 * `undo()`/`redo()` 遵循和单个创建命令一样的补偿语义（见文件头注释）：
 * `redo()` 重新调用 `create()`，产生的是全新的 id，不是"复活"上次撤销时
 * 归档的那些。
 */
export function createBatchCommand<Input>(opts: {
  readonly label: string;
  readonly inputs: readonly Input[];
  readonly create: (input: Input) => Promise<{ readonly id: string }>;
  readonly archive: (id: string) => Promise<unknown>;
  /** 本次成功创建/补偿归档之后，让调用方去做场景刷新、选中态更新之类的收尾。 */
  readonly onCreated?: (ids: readonly string[]) => void | Promise<void>;
  readonly onArchived?: (ids: readonly string[]) => void | Promise<void>;
}): Command {
  let createdIds: string[] = [];
  return {
    label: opts.label,
    structural: true,
    do: async () => {
      const ids: string[] = [];
      try {
        for (const input of opts.inputs) {
          const res = await opts.create(input);
          ids.push(res.id);
        }
      } catch (err) {
        if (ids.length > 0) {
          const compensation = await Promise.allSettled(ids.map((id) => opts.archive(id)));
          await opts.onArchived?.(ids);
          if (compensation.some((r) => r.status === 'rejected')) {
            throw new PartialBatchCreateCompensationFailedError(ids.length, err);
          }
        }
        throw err;
      }
      createdIds = ids;
      await opts.onCreated?.(ids);
    },
    undo: async () => {
      // M3B 独立复审第二轮 NEW-3：不能在循环开始前就把 `createdIds` 清空——
      // 如果中间某个 `archive()` 失败（`CommandStack.undo()` 修复后会把这条
      // 命令原样留在 `past` 里，允许用户再点一次撤销重试），必须保证重试时
      // `createdIds` 还记得"哪些还没归档成功"，而不是已经被清成 `[]`、
      // 让重试变成一次什么都不做的空跑。
      const ids = createdIds;
      const results = await Promise.allSettled(ids.map((id) => opts.archive(id)));
      const archived = ids.filter((_, i) => results[i]!.status === 'fulfilled');
      const stillPending = ids.filter((_, i) => results[i]!.status === 'rejected');
      createdIds = stillPending;
      if (archived.length > 0) await opts.onArchived?.(archived);
      if (stillPending.length > 0) {
        const firstError = results.find(
          (r): r is PromiseRejectedResult => r.status === 'rejected',
        )!.reason;
        throw new PartialBatchCreateCompensationFailedError(stillPending.length, firstError);
      }
    },
  };
}
